import { useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type SupportedLocale = 'en' | 'es';

const SUPPORTED_LOCALES: SupportedLocale[] = ['en', 'es'];
const DEFAULT_LOCALE: SupportedLocale = 'en';
const STORAGE_KEY = 'presencia_language';

export const useLocale = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  const getLocaleFromQuery = useCallback((): SupportedLocale | null => {
    const params = new URLSearchParams(location.search);
    const locale = params.get('locale');
    if (locale && SUPPORTED_LOCALES.includes(locale as SupportedLocale)) {
      return locale as SupportedLocale;
    }
    return null;
  }, [location.search]);

  const currentLocale = (i18n.language?.split('-')[0] || DEFAULT_LOCALE) as SupportedLocale;

  const changeLanguage = useCallback(
    (locale: SupportedLocale) => {
      if (SUPPORTED_LOCALES.includes(locale)) {
        i18n.changeLanguage(locale);
        localStorage.setItem(STORAGE_KEY, locale);

        const params = new URLSearchParams(location.search);
        params.set('locale', locale);
        history.replace({
          pathname: location.pathname,
          search: params.toString(),
        });
      }
    },
    [i18n, history, location.pathname, location.search]
  );

  useEffect(() => {
    const queryLocale = getLocaleFromQuery();
    if (queryLocale && queryLocale !== currentLocale) {
      i18n.changeLanguage(queryLocale);
      localStorage.setItem(STORAGE_KEY, queryLocale);
    }
  }, [getLocaleFromQuery, currentLocale, i18n]);

  return {
    currentLocale,
    changeLanguage,
    supportedLocales: SUPPORTED_LOCALES,
    isSpanish: currentLocale === 'es',
    isEnglish: currentLocale === 'en',
  };
};
